import { Activity, Instructions } from '@go-modules/interfaces/activity';
import type { OrgSettings } from '@go-modules/interfaces/groups/org-settings';
import { Task, TaskType } from '@go-modules/interfaces/task';
import { escapeHtml } from '@go-modules/common/helpers/utils';
import type { ActivityEditorEnvironment } from './activity-editor-context';

interface ActivityPreviewEditorDialogDataBase {
	tasks?: Task[];
	initialTaskType?: TaskType;
	group?: unknown;
	account?: unknown;
	org?: unknown;
	license?: unknown;
	environment?: ActivityEditorEnvironment;
	libraryContext?: {
		addToCollection?: number;
		addToFolder?: number | null;
		isOrgCollectionAndCanModifyItems?: boolean;
		orgSettings?: Partial<OrgSettings>;
	};
	isSaveRequired?: boolean;
	onNavigateBack?: () => void;
	focusReturnSelector?: string;
	syncSelectedActivity?: ActivityPreviewEditorSelectedActivitySync;
}

interface ActivityPreviewEditorCreateDialogData extends ActivityPreviewEditorDialogDataBase {
	mode: 'create';
	activityDraft: Activity;
	activityId?: never;
}

interface ActivityPreviewEditorEditDialogDataById extends ActivityPreviewEditorDialogDataBase {
	mode: 'edit';
	activityId: number;
	activityDraft?: Activity;
}

interface ActivityPreviewEditorEditDialogDataByDraft extends ActivityPreviewEditorDialogDataBase {
	mode: 'edit';
	activityId?: number;
	activityDraft: Activity;
}

export type ActivityPreviewEditorDialogData =
	ActivityPreviewEditorCreateDialogData |
	ActivityPreviewEditorEditDialogDataById |
	ActivityPreviewEditorEditDialogDataByDraft;

export interface ActivityPreviewEditorSelectedActivitySync {
	activityId: number;
	sync(activity: Activity): void;
}

export interface ActivityPreviewEditorDialogResult {
	activity: Activity;
	tasks: Task[];
	mode: 'create' | 'edit';
}

export interface ActivityPreviewEditorSavePayload extends Activity {
	tasks: Task[];
	add_to_collection?: number;
	add_to_folder?: number | null;
}

export interface ActivityPreviewEditorDialogDismissedResult {
	dismissed: true;
}

export type ActivityPreviewEditorDialogCloseResult =
	ActivityPreviewEditorDialogResult |
	ActivityPreviewEditorDialogDismissedResult |
	null |
	undefined;

export type ActivityPreviewEditorPreviewPanel = 'details' | 'task';

export function getActivityPreviewEditorInitialPreviewPanel (
	selectedTaskType: TaskType | null
): ActivityPreviewEditorPreviewPanel {
	return selectedTaskType == null ? 'details' : 'task';
}

export function getActivityPreviewEditorDialogDataError (data: ActivityPreviewEditorDialogData): string | null {
	if (data.mode === 'create' && data.activityDraft == null) {
		return 'Create activity preview editor dialogs require an activityDraft.';
	}

	if (data.mode === 'edit' && data.activityId == null && data.activityDraft == null) {
		return 'Edit activity preview editor dialogs require an activityId or activityDraft.';
	}

	return null;
}

export function syncSelectedActivityAfterSave (
	syncSelectedActivity: ActivityPreviewEditorSelectedActivitySync | undefined,
	activity: Activity
): void {
	if (syncSelectedActivity == null || syncSelectedActivity.activityId !== activity.activity_id) {
		return;
	}

	syncSelectedActivity.sync(activity);
}

export function buildActivityPreviewEditorSavePayload (
	data: ActivityPreviewEditorDialogData,
	activity: Activity,
	tasks: Task[]
): ActivityPreviewEditorSavePayload {
	const payload: ActivityPreviewEditorSavePayload = {
		...activity,
		is_conversation: activity.is_conversation ?? false,
		peer_rubric_critique_enabled: activity.peer_rubric_critique_enabled ?? false,
		self_rubric_critique_enabled: activity.self_rubric_critique_enabled ?? false,
		peer_rubric_id: activity.peer_rubric_id ?? null,
		recording_instructions: resolveActivityInstructions(activity.recording_instructions),
		feedback_instructions: resolveActivityInstructions(activity.feedback_instructions),
		tasks
	};

	if (data.mode === 'create') {
		if (data.libraryContext?.addToCollection != null) {
			payload.add_to_collection = data.libraryContext.addToCollection;
		}
		if (data.libraryContext?.addToFolder !== undefined) {
			payload.add_to_folder = data.libraryContext.addToFolder;
		}
	}

	return payload;
}

export function getActivityPreviewEditorSaveErrorMessage (error: unknown): string {
	const errorBody = getHttpErrorBody(error);
	if (typeof errorBody?.message === 'string' && errorBody.message !== '') {
		return escapeHtml(errorBody.message);
	}

	const validationError = getFirstValidationError(errorBody?.errors);
	return validationError == null ? 'common_unknown-error_message' : escapeHtml(validationError);
}

function resolveActivityInstructions (instructions: Instructions | null | undefined): Instructions | null {
	if (instructions == null) {
		return null;
	}

	const instructionRecord = instructions as unknown as Record<string, unknown>;
	if (!instructionRecord.media_id && !instructionRecord.text) {
		return null;
	}

	return instructions;
}

function getHttpErrorBody (error: unknown): { message?: unknown; errors?: unknown } | null {
	const httpError = error as { error?: unknown } | null;
	if (typeof httpError?.error === 'string' && httpError.error !== '') {
		return {message: httpError.error};
	}

	if (httpError?.error != null && typeof httpError.error === 'object') {
		return httpError.error as { message?: unknown; errors?: unknown };
	}

	return null;
}

function getFirstValidationError (errors: unknown): string | null {
	if (errors == null || typeof errors !== 'object') {
		return null;
	}

	for (const error of Object.values(errors as Record<string, unknown>)) {
		if (typeof error === 'string') {
			return error;
		}

		if (Array.isArray(error)) {
			const firstMessage = error.find((message) => typeof message === 'string');
			if (firstMessage != null) {
				return firstMessage;
			}
		}
	}

	return null;
}
